import { useCallback, useEffect, useMemo, useState } from 'react';

import { Card, Skeleton } from 'antd';
import classNames from 'classnames';
import ReactECharts from 'echarts-for-react';

import { formatMetricValue } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/Icon';

import {
  createPodMetricBarChartOption,
  createPodMetricLineChartOption,
  type PodMetricLineChartPodHistory,
} from '@/widgets/monitoring/shared/createPodMetricChartOption';

import styles from './MetricCard.module.css';

type PodHistory = {
  pod: string;
  data: { timestamp: number; value: number }[];
  color: string;
};

type MetricCardProps = {
  title: string;
  value: number;
  unit: string;
  isLoading?: boolean;
  totalCapacity?: number;
  percentage?: number;
  secondaryText?: string;
  podHistory: PodHistory[];
  onDetailClick?: () => void;
};

export const getMetricCardChartKey = (
  title: string,
  chartType: 'line' | 'bar',
  podHistory: PodMetricLineChartPodHistory[]
) => {
  return `metric-${title}-${chartType}-${podHistory.map((pod) => `${pod.pod}-${pod.color}`).join(',')}`;
};

export const MetricCard = ({
  title,
  value,
  unit,
  isLoading = false,
  totalCapacity,
  percentage,
  secondaryText,
  podHistory,
  onDetailClick,
}: MetricCardProps) => {
  const [selectedSeries, setSelectedSeries] = useState<Record<string, boolean>>({});
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  const podNames = useMemo(() => podHistory.map((p) => p.pod).join(','), [podHistory]);

  const visiblePodHistory = useMemo(() => {
    return podHistory.filter((pod) => selectedSeries[pod.pod] !== false);
  }, [podHistory, selectedSeries]);

  const chartOption = useMemo(() => {
    if (!visiblePodHistory.length) return {};

    if (chartType === 'line') {
      return createPodMetricLineChartOption({ podHistory: visiblePodHistory, unit });
    }

    return createPodMetricBarChartOption({ podHistory: visiblePodHistory, unit });
  }, [chartType, unit, visiblePodHistory]);

  const formattedPrimaryValue =
    percentage !== undefined ? `${formatMetricValue(percentage)}%` : formatMetricValue(value);
  const formattedSecondaryValue =
    secondaryText ??
    (totalCapacity !== undefined
      ? `(${formatMetricValue(value)} / ${formatMetricValue(totalCapacity)} ${unit})`
      : unit);

  const handleLegendClick = useCallback((podName: string) => {
    setSelectedSeries((prev) => {
      const newSelected = { ...prev, [podName]: !prev[podName] };
      return newSelected;
    });
  }, []);

  useEffect(() => {
    if (!podNames) return;

    setSelectedSeries((prev) => {
      const newSelected: Record<string, boolean> = {};
      const currentPods = podNames.split(',');

      currentPods.forEach((podName) => {
        newSelected[podName] = prev[podName] ?? true;
      });

      return newSelected;
    });
  }, [podNames]);

  if (isLoading) {
    return (
      <Card className={styles.card}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </Card>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>{title}</span>
          {onDetailClick && (
            <button
              type="button"
              className={styles.actionButton}
              onClick={onDetailClick}
              aria-label={`${title} 상세 보기`}>
              <Icon name="open-in-new" size={16} className={styles.actionIcon} />
            </button>
          )}
        </div>
        <div className={styles.chartTypeGroup}>
          <button
            type="button"
            className={classNames(styles.chartTypeButton, {
              [styles.chartTypeButtonActive]: chartType === 'line',
            })}
            onClick={() => setChartType('line')}
            aria-pressed={chartType === 'line'}
            aria-label={`${title} 선형 차트`}>
            <Icon name="chart-line" size={16} className={styles.chartTypeIcon} />
          </button>
          <button
            type="button"
            className={classNames(styles.chartTypeButton, {
              [styles.chartTypeButtonActive]: chartType === 'bar',
            })}
            onClick={() => setChartType('bar')}
            aria-pressed={chartType === 'bar'}
            aria-label={`${title} 막대 차트`}>
            <Icon name="chart-bar" size={16} className={styles.chartTypeIcon} />
          </button>
        </div>
      </div>
      <div className={styles.metricSummary}>
        <span
          className={classNames(styles.metricValue, {
            [styles.metricValueAccent]: percentage !== undefined,
            [styles.metricValueDefault]: percentage === undefined,
          })}>
          {formattedPrimaryValue}
        </span>
        <span className={styles.metricMeta}>{formattedSecondaryValue}</span>
      </div>
      <div className={`${styles.chart} ${chartType === 'bar' ? styles.chartBar : styles.chartLine}`}>
        <ReactECharts
          key={getMetricCardChartKey(title, chartType, visiblePodHistory)}
          option={chartOption}
          style={{
            height: '220px',
            width: '100%',
          }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          lazyUpdate={false}
        />
      </div>

      <div className={styles.legendContainer}>
        {podHistory.map((pod) => {
          const isSelected = selectedSeries[pod.pod] !== false;

          return (
            <button
              type="button"
              key={pod.pod}
              className={`${styles.legendItem} ${!isSelected && styles.legendItemInactive}`}
              onClick={() => handleLegendClick(pod.pod)}
              aria-pressed={isSelected}
              aria-label={`${pod.pod} 범례 토글`}>
              <span className={styles.legendMarkerWrapper}>
                <span className={styles.legendMarker} style={{ backgroundColor: pod.color }} />
              </span>
              <span className={styles.legendText} title={pod.pod}>
                {pod.pod}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
